import { SEARCH_IMAGE_MAX_DIMENSION } from "./AssetSearch";
import { DEFAULT_PRICING_SETTINGS, type PricingSettings } from "./PricingSettings";

/**
 * Everything the cost model needs beyond the settings file: physical and published constants, and
 * traffic-dependent values the admin pricing endpoint measures (with a planning fallback until
 * there is data). Resolved into one PricingAssumptions object for ProviderPricing and
 * PricingEconomics.
 */

/** Values fixed by the code or by a published price list, with where each comes from. */
export const PRICING_CONSTANTS = {
  /** Average characters per text token; the tokenizer heuristic the public price list uses too. */
  charactersPerToken: 4,
  /** Pixels per embedded preview or sampled frame: the square SEARCH_IMAGE_MAX_DIMENSION bound. */
  searchIndexingPixelsPerUnit: SEARCH_IMAGE_MAX_DIMENSION * SEARCH_IMAGE_MAX_DIMENSION,
  /** Token allowance per search query embedding (the query and the retrieval instruction). */
  searchQueryTokens: 128,
  /** Share of metered asset reads that miss the Cloudflare cache; 1 prices every read at origin. */
  assetReadOriginFetchShare: 1,
  paymentFees: {
    /** Stripe standard UK card rate. */
    cardPercentage: 0.015,
    /** Stripe Billing pay-as-you-go rate of billing volume. */
    billingPercentage: 0.007,
    /** Stripe fixed fee per successful UK card charge, in GBP. */
    fixedPerInvoiceGbp: 0.2,
  },
} as const;

export const PRICING_CONSTANT_DESCRIPTIONS: Record<string, { label: string; description: string }> =
  {
    charactersPerToken: {
      label: "Characters per token",
      description:
        "Average characters per text token (tokenizer heuristic), used for Azure's 1,000-character records.",
    },
    searchIndexingPixelsPerUnit: {
      label: "Search indexing pixels per frame",
      description: `${SEARCH_IMAGE_MAX_DIMENSION}px square bound on each embedded preview or sampled frame (SEARCH_IMAGE_MAX_DIMENSION).`,
    },
    searchQueryTokens: {
      label: "Search query tokens",
      description: "Token allowance per submitted search, including the retrieval instruction.",
    },
    assetReadOriginFetchShare: {
      label: "Asset read origin fetch share",
      description:
        "Share of metered asset reads priced as cache misses reading from R2; 1 prices every read at origin.",
    },
    "paymentFees.cardPercentage": {
      label: "Card fee percentage",
      description: "Stripe standard UK card rate (https://stripe.com/gb/pricing).",
    },
    "paymentFees.billingPercentage": {
      label: "Stripe Billing percentage",
      description:
        "Stripe Billing pay-as-you-go rate of billing volume (https://stripe.com/gb/pricing).",
    },
    "paymentFees.fixedPerInvoiceGbp": {
      label: "Fixed fee per invoice (GBP)",
      description:
        "Stripe fixed fee per successful UK card charge (https://stripe.com/gb/pricing).",
    },
  };

export const PRICING_MEASUREMENT_KEYS = [
  "usdToGbp",
  "averageInvoiceGbp",
  "averageTextRequestTokens",
] as const;
export type PricingMeasurementKey = (typeof PRICING_MEASUREMENT_KEYS)[number];

/** A value measured by the admin pricing endpoint; null when there is nothing to measure yet. */
export interface PricingMeasurement {
  value: number | null;
  /** Where the value came from, or why it is missing. */
  detail: string;
}
export type PricingMeasurements = Record<PricingMeasurementKey, PricingMeasurement>;

/** Planning values used until a measurement exists. */
export const PRICING_MEASUREMENT_FALLBACKS: Record<PricingMeasurementKey, number> = {
  usdToGbp: 0.75,
  averageInvoiceGbp: 20,
  averageTextRequestTokens: 250,
};

export const PRICING_MEASUREMENT_DESCRIPTIONS: Record<
  PricingMeasurementKey,
  { label: string; description: string }
> = {
  usdToGbp: {
    label: "USD to GBP",
    description: "Exchange rate used to convert USD provider prices to GBP.",
  },
  averageInvoiceGbp: {
    label: "Average invoice (GBP)",
    description: "Average paid invoice, used to spread Stripe's fixed fee across revenue.",
  },
  averageTextRequestTokens: {
    label: "Average text request tokens",
    description:
      "Average billable tokens per text moderation request (the 250-token minimum is applied on top).",
  },
};

/** The measurements as the endpoint reports them before any data exists. */
export const UNMEASURED_PRICING_MEASUREMENTS: PricingMeasurements = Object.fromEntries(
  PRICING_MEASUREMENT_KEYS.map((key) => [key, { value: null, detail: "Not measured." }]),
) as PricingMeasurements;

/** The complete input of the cost model: settings, constants and resolved measurements. */
export interface PricingAssumptions extends PricingSettings {
  charactersPerToken: number;
  searchIndexingPixelsPerUnit: number;
  searchQueryTokens: number;
  assetReadOriginFetchShare: number;
  paymentFees: {
    cardPercentage: number;
    billingPercentage: number;
    fixedPerInvoiceGbp: number;
    averageInvoiceGbp: number;
  };
  usdToGbp: number;
  averageTextRequestTokens: number;
}

export const resolvePricingMeasurement = (
  measurements: Partial<PricingMeasurements> | undefined,
  key: PricingMeasurementKey,
): number => measurements?.[key]?.value ?? PRICING_MEASUREMENT_FALLBACKS[key];

/** Combines the settings file, the code constants and the measurements (falling back per value). */
export const resolvePricingAssumptions = (
  settings: PricingSettings,
  measurements?: Partial<PricingMeasurements>,
): PricingAssumptions => ({
  ...settings,
  charactersPerToken: PRICING_CONSTANTS.charactersPerToken,
  searchIndexingPixelsPerUnit: PRICING_CONSTANTS.searchIndexingPixelsPerUnit,
  searchQueryTokens: PRICING_CONSTANTS.searchQueryTokens,
  assetReadOriginFetchShare: PRICING_CONSTANTS.assetReadOriginFetchShare,
  paymentFees: {
    ...PRICING_CONSTANTS.paymentFees,
    averageInvoiceGbp: resolvePricingMeasurement(measurements, "averageInvoiceGbp"),
  },
  usdToGbp: resolvePricingMeasurement(measurements, "usdToGbp"),
  averageTextRequestTokens: resolvePricingMeasurement(measurements, "averageTextRequestTokens"),
});

/** The settings file with constants and fallbacks: what the public price list and tests use. */
export const DEFAULT_PRICING_ASSUMPTIONS = resolvePricingAssumptions(DEFAULT_PRICING_SETTINGS);
